import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { rRemovePost, rUpdatePost } from "services/redux/postForm/postForm";
import { AppDispatch, RootState } from "services/redux/store";
import {
    FormGifViewer,
    FormImageViewer,
    FormPostControls,
    FormVideoViewer,
    Poll,
    TextArea,
    customToaster
} from "ui";
import {
    constructDuodate,
    destructDuoDate
} from "utils/helpers/duedateHelpers";
import {
    CustomComponentProps,
    PollObject,
    PollType,
    PostFormObject
} from "utils/types/generalTypes";

export type PostFormProps = CustomComponentProps & {
    postId: number;
    post: PostFormObject;
};

const PostForm = ({ postId, post, className, style }: PostFormProps) => {
    const mode = useSelector((state: RootState) => state.site.mode);
    const dispatch = useDispatch<AppDispatch>();
    const { avatar } = useSelector((state: RootState) => state.account);

    // ------------------------- validate all post on update any field
    const toggleValid = () => {
        if (
            !post.text.length &&
            !post.images &&
            !post.video &&
            !post.gif &&
            !post.poll &&
            post.isValid
        ) {
            dispatch(rUpdatePost({ ...post, isValid: false }));

            return;
        }

        // validate poll if it's text poll
        const maxDueDate = 7 * 24 * 60 + 23 * 60 + 59;
        if (
            post.poll &&
            (post.poll.dueDate < 5 ||
                post.poll.dueDate > maxDueDate ||
                (post.poll.type == "text" &&
                    (!(typeof post.poll.firstOption == "string") ||
                        post.poll.firstOption.length < 1 ||
                        post.poll.firstOption.length > 25 ||
                        !(typeof post.poll.secondOption == "string") ||
                        post.poll.secondOption.length < 1 ||
                        post.poll.secondOption.length > 25 ||
                        (post.poll.hasThirdOption &&
                            (!(typeof post.poll.thirdOption == "string") ||
                                post.poll.thirdOption.length < 1 ||
                                post.poll.thirdOption.length > 25)))))
        ) {
            dispatch(rUpdatePost({ ...post, isValid: false }));
            return;
        }
        if (
            post.poll &&
            (post.poll.dueDate < 5 ||
                post.poll.dueDate > maxDueDate ||
                (post.poll.type == "img" &&
                    (post.poll.firstOption.length > 1 ||
                        (post.poll.firstOption as FileList)[0] == undefined ||
                        !(post.poll.firstOption as FileList)[0].name.match(
                            /\.(jpg|jpeg|png|gif)$/
                        ) ||
                        post.poll.secondOption.length > 1 ||
                        (post.poll.secondOption as FileList)[0] == undefined ||
                        !(post.poll.secondOption as FileList)[0].name.match(
                            /\.(jpg|jpeg|png|gif)$/
                        ) ||
                        (post.poll.hasThirdOption &&
                            (post.poll.thirdOption == undefined ||
                                post.poll.thirdOption.length > 1 ||
                                (post.poll.thirdOption as FileList) ==
                                    undefined ||
                                (post.poll.thirdOption as FileList)[0] ==
                                    undefined ||
                                !(
                                    post.poll.thirdOption as FileList
                                )[0].name.match(/\.(jpg|jpeg|png|gif)$/))))))
        ) {
            // updatePost(postId, { ...post, isValid: false });
            dispatch(rUpdatePost({ ...post, isValid: false }));
            return;
        }

        // set post to be valid
        if (!post.isValid) {
            dispatch(rUpdatePost({ ...post, isValid: true }));
            return;
        }
    };

    const handleRemoveMedia = () => {
        dispatch(
            rUpdatePost({
                ...post,
                images: undefined,
                mediaType: null,
                video: undefined,
                poll: undefined,
                gif: undefined
            })
        );
    };

    useEffect(() => {
        toggleValid();
    }, [post.text, post.images, post.video, post.gif, post.poll]);

    // ------------------------- validate text part
    const handleChangeText = (val: string) => {
        if (val.length > 250) {
            return;
        } else {
            // updatePost(postId, { ...post, text: val });
            dispatch(rUpdatePost({ ...post, text: val }));
        }
    };

    // ------------------------- validate image part
    const handleChangeImage = (images: FileList) => {
        // validate image
        if (!images[0]) {
            return;
        }
        if (images.length > 4) {
            customToaster("error", "You can select up to 4 images only", mode);
            return;
        }

        const imagesArr: File[] = [];
        let valid = true;
        // eslint-disable-next-line
        for (let image of Array.from(images)) {
            if (!image.name.match(/\.(jpg|jpeg|png)$/)) {
                valid = false;
                break;
            } else {
                imagesArr.push(image);
            }
        }
        if (!valid) {
            customToaster("error", "Please select valid imag", mode);
            return;
        }
        dispatch(
            rUpdatePost({
                ...post,
                images: imagesArr,
                mediaType: "img",
                video: undefined,
                poll: undefined,
                gif: undefined
            })
        );
        // toggleValid();
    };

    const removeImg = (img: File) => {
        if (!post.images) {
            return;
        }

        const imagesArr = post.images.filter((i) => i != img);

        if (imagesArr.length) {
            dispatch(
                rUpdatePost({
                    ...post,
                    images: imagesArr,
                    mediaType: "img",
                    video: undefined,
                    poll: undefined,
                    gif: undefined
                })
            );
        } else {
            dispatch(
                rUpdatePost({
                    ...post,
                    images: undefined,
                    mediaType: null,
                    video: undefined,
                    poll: undefined,
                    gif: undefined
                })
            );
        }
        // toggleValid();
    };

    // ------------------------- validate video part
    const handleChangeVideo = (videos: FileList) => {
        if (!videos[0]) {
            return;
        }
        if (videos.length > 1) {
            customToaster("error", "You can select 1 video only", mode);
            return;
        }

        if (!videos[0].name.match(/\.(mp4|mkv|webm|3gp)$/)) {
            customToaster("error", "Please select valid Video", mode);
        } else {
            dispatch(
                rUpdatePost({
                    ...post,
                    video: videos[0],
                    mediaType: "vid",
                    images: undefined,
                    poll: undefined,
                    gif: undefined
                })
            );
        }

        // toggleValid();
    };

    // ------------------------- validate poll part
    const updatePollType = (type: PollType) => {
        const newPoll: PollObject = {
            type: type,
            firstOption: "",
            secondOption: "",
            thirdOption: undefined,
            hasThirdOption: false,
            dueDate: 5
        };
        dispatch(
            rUpdatePost({
                ...post,
                poll: newPoll,
                // mediaType:""
                images: undefined,
                video: undefined,
                gif: undefined,
                isValid: false
            })
        );
    };

    const setPoll = () => {
        updatePollType("text");
    };

    const updatePollOption = (
        option: string | FileList,
        optionOrder: 0 | 1 | 2
    ) => {
        // check if the poll is added in the first place
        if (!post.poll) {
            return;
        }
        // check if poll type is text and the input is valid
        if (
            post.poll.type == "text" &&
            (!(typeof option == "string") || option.length > 25)
        ) {
            return;
        }
        // check if poll type is image and validate it
        if (
            post.poll.type == "img" &&
            (option.length > 1 ||
                (option as FileList) == undefined ||
                (option as FileList)[0] == undefined ||
                !(option as FileList)[0].name.match(/\.(jpg|jpeg|png|gif)$/))
        ) {
            return;
        }
        // push the element
        if (optionOrder === 0) {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, firstOption: option }
                })
            );
        } else if (optionOrder === 1) {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, secondOption: option }
                })
            );
        } else if (optionOrder === 2) {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, thirdOption: option }
                })
            );
        }
    };

    const handleShowThirdPoll = () => {
        if (!post.poll) {
            return;
        }
        dispatch(
            rUpdatePost({
                ...post,
                poll: {
                    ...post.poll,
                    thirdOption: "",
                    hasThirdOption: true
                }
            })
        );
    };

    const handleRemoveThirdPoll = () => {
        if (!post.poll) {
            return;
        }
        dispatch(
            rUpdatePost({
                ...post,
                poll: {
                    ...post.poll,
                    thirdOption: undefined,
                    hasThirdOption: false
                }
            })
        );
    };

    const updatePollMinutes = (mins: number) => {
        // check if theres hourse or days if not check if yhe minutes is not less than 5
        let min = 0;
        if (!post.poll) {
            return;
        }
        if (post.poll.dueDate < 60) {
            min = 5;
        }
        // if it's not less than min or above 60
        if (mins < min || mins > 59) {
            return;
        }
        const { days, hours } = destructDuoDate(post.poll.dueDate);
        const dueDate = constructDuodate(days, hours, mins);
        dispatch(
            rUpdatePost({
                ...post,
                poll: { ...post.poll, dueDate }
            })
        );
        // toggleValid();
    };
    const updatePollHours = (h: number) => {
        if (!post.poll) {
            return;
        }
        // if it's not less than min or above 60
        if (h < 0 || h > 23) {
            return;
        }
        const { days, minutes } = destructDuoDate(post.poll.dueDate);
        const dueDate = constructDuodate(days, h, minutes);
        if (dueDate < 5) {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, dueDate: 5 }
                })
            );
        } else {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, dueDate }
                })
            );
        }
    };

    const updatePollDays = (d: number) => {
        if (!post.poll) {
            return;
        }
        // if it's not less than min or above 60
        if (d < 0 || d > 7) {
            return;
        }
        const { hours, minutes } = destructDuoDate(post.poll.dueDate);
        const dueDate = constructDuodate(d, hours, minutes);
        if (dueDate < 5) {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, dueDate: 5 }
                })
            );
        } else {
            dispatch(
                rUpdatePost({
                    ...post,
                    poll: { ...post.poll, dueDate }
                })
            );
        }
    };

    // ------------------------- handle remove
    const handleRemovePost = () => {
        if (postId == 1) {
            return;
        }
        dispatch(rRemovePost(postId));
    };

    return (
        <div
            className={`post-form d-flex flex-j-start gap-10 flex-a-start p-relative ${className} `}
            style={style}
        >
            <div className="post-form-v-line"></div>
            <div className="post-form-avatar p-relative">
                <div className="img-col navbar-item is-account">
                    <a href="#" className="d-flex flex-a-center">
                        <img src={avatar} alt="profile picture" />
                    </a>
                </div>
            </div>

            <div className="post-form-viewer">
                <TextArea
                    text={post.text}
                    handleChangeText={handleChangeText}
                />

                {/* media viewer part  */}
                <div className="media-displayer pt-10 pb-10">
                    {/* selected image viewer   */}
                    {post.images ? (
                        <FormImageViewer
                            images={post.images}
                            removeImg={removeImg}
                        />
                    ) : (
                        ""
                    )}

                    {/* selected video viewer */}
                    {post.video ? (
                        <FormVideoViewer
                            video={post.video}
                            handleRemoveMedia={handleRemoveMedia}
                        />
                    ) : (
                        ""
                    )}

                    {/* gif viewer  */}
                    {post.gif ? (
                        <FormGifViewer
                            gif={post.gif}
                            handleRemoveMedia={handleRemoveMedia}
                        />
                    ) : (
                        ""
                    )}

                    {/* poll post   */}
                    {post.poll ? (
                        <Poll
                            poll={post.poll}
                            updatePollType={updatePollType}
                            updatePollOption={updatePollOption}
                            handleRemoveMedia={handleRemoveMedia}
                            handleShowThirdPoll={handleShowThirdPoll}
                            handleRemoveThirdPoll={handleRemoveThirdPoll}
                            updatePollDays={updatePollDays}
                            updatePollHours={updatePollHours}
                            updatePollMinutes={updatePollMinutes}
                        />
                    ) : (
                        ""
                    )}
                </div>

                {/* media import btns  */}
                <FormPostControls
                    handleChangeImage={handleChangeImage}
                    handleChangeVideo={handleChangeVideo}
                    setPoll={setPoll}
                    postId={postId}
                    postType="post"
                    handleRemovePost={handleRemovePost}
                />
            </div>
        </div>
    );
};

export default PostForm;
